import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { guardExternal, kritisOn } from '../lib/policy';
import { X, QrCode, Loader2, Share2, Copy, Download, Route } from 'lucide-react';
import { uid } from '../lib/model';
import { openPlanTab } from './lpShared';

// share a planned deployment round by short link / QR; colleague imports it into his device
const API = `${process.env.REACT_APP_BACKEND_URL}/api/round/share`;
const KEY = 'giss_round_in';
const q = new URLSearchParams(window.location.search).get('round');
if (q && /^[A-Z0-9]{6}$/i.test(q)) {
  sessionStorage.setItem(KEY, q.toUpperCase());
  const u = new URL(window.location.href); u.searchParams.delete('round'); window.history.replaceState(null, '', u.pathname + u.search);
}
const linkOf = (code) => `${window.location.origin}/?round=${code}`;

export function ShareRoundDialog({ t, project, plan, onClose }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const entries = plan.filter((p) => !p.doneAt).map((p) => ({ name: p.name.slice(0, 80), lat: p.lat, lon: p.lon, mode: p.mode, days: p.days || 1, addr: (p.address || '').slice(0, 200) || null }));
    Promise.resolve().then(() => { guardExternal(); return fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: (project.title || '').slice(0, 120), entries }) }); })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(async (r) => setRes({ ...r, qr: await QRCode.toDataURL(linkOf(r.code), { width: 520, margin: 2, errorCorrectionLevel: 'M' }) }))
      .catch(() => setErr(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const text = res && `${t.rsMsg} ${project.title || ''} (${res.count}): ${linkOf(res.code)}`;
  const share = async () => { try { await navigator.share({ title: t.rsTitle, text }); } catch { /* cancelled */ } };
  const copy = async () => { try { await navigator.clipboard.writeText(linkOf(res.code)); toast.success(t.rsCopied); } catch { toast.error(linkOf(res.code)); } };
  return createPortal(
    <div className="editor-backdrop" data-testid="round-share-dialog">
      <div className="editor cat-share">
        <div className="editor-head">
          <h4><QrCode size={18} /> {t.rsTitle}</h4>
          <button className="icon-btn" data-testid="round-share-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        {err && <div className="empty" data-testid="round-share-error">{t.rsErr}</div>}
        {!err && !res && <div className="empty"><Loader2 className="spin" size={22} /></div>}
        {res && (
          <>
            <img className="share-qr" src={res.qr} alt="QR" data-testid="round-share-qr" />
            <div className="share-code" data-testid="round-share-code">{res.code}</div>
            <p className="muted small-hint" data-testid="round-share-info">{res.count} {t.lrPlanned} · {t.rsHint}</p>
            <div className="row">
              {navigator.share && <button className="btn btn-primary" data-testid="round-share-native-btn" onClick={share}><Share2 size={16} /> {t.rsSend}</button>}
              <a className="btn btn-green" data-testid="round-share-wa-btn" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer">WhatsApp</a>
              <button className="btn btn-outline" data-testid="round-share-copy-btn" onClick={copy}><Copy size={16} /> {t.rsCopy}</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function RoundImport({ t, project, updateProject }) {
  const [data, setData] = useState(null);
  const code = sessionStorage.getItem(KEY);
  useEffect(() => {
    if (!code) return;
    if (!project) { toast.info(t.rsNeedProj, { id: 'rs-need', duration: 8000 }); return; }
    if (kritisOn()) { sessionStorage.removeItem(KEY); toast.error(t.krBlocked); return; }
    fetch(`${API}/${code}`).then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setData({ ...d, code }))
      .catch((s) => { if (s === 404) { sessionStorage.removeItem(KEY); toast.error(t.rsImpErr); } else toast.error(t.rsErr); });
  }, [code, !!project]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data || !project) return null;
  const done = () => { sessionStorage.removeItem(KEY); setData(null); };
  const take = () => {
    const add = data.entries.map((e) => ({ id: uid('lp'), name: e.name, address: e.addr || '', lat: e.lat, lon: e.lon, mode: e.mode, days: e.days, shared: data.code }));
    updateProject((p) => ({ ...p, loggerPlan: [...(p.loggerPlan || []), ...add] }));
    done(); toast.success(`${t.rsImpOk}: ${add.length}`); setTimeout(() => openPlanTab('deploy'), 150);
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="round-import-dialog">
      <div className="editor cat-share">
        <div className="editor-head"><h4><Route size={18} /> {t.rsImpTitle}</h4></div>
        <p data-testid="round-import-info"><b>{data.title || t.lrTitle}</b> · {data.entries.length} {t.lrPlanned}</p>
        <ol className="lp-round-list">{data.entries.slice(0, 50).map((e, i) => <li key={i}><i>{i + 1}</i>{e.name}<small>{e.mode === 'perm' ? t.lpPerm : `${e.days} ${t.lgDays}`}</small></li>)}</ol>
        <p className="muted small-hint">{t.rsImpHint} „{project.title}“.</p>
        <div className="row">
          <button className="btn btn-green" data-testid="round-import-accept-btn" onClick={take}><Download size={16} /> {t.rsImpBtn}</button>
          <button className="btn btn-outline" data-testid="round-import-decline-btn" onClick={done}>{t.rsImpNo}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
